<template>
  <div>
    <reports-warning/>
    <div>
      <div class="w-full flex mb-4">
        <report-date-picker v-model="dateRange" @change="getReports"/>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 grid-cols-3 gap-4">
        <stats-card :title="getTotalFor('total', 'jobTotals')"
                    :previous-title="getTotalFor('total', 'previousJobTotals')"
                    :loading="loading"
                    class="col-span-1"
                    :small-title="'Total Jobs'"
                    type="info"
                    icon="nc-icon nc-single-copy-04">
        </stats-card>

        <stats-card
          :loading="loading"
          class="col-span-1"
          :small-title="'Most active client'"
          type="success"
          icon="nc-icon nc-spaceship">
          <template v-slot:title>
            {{getMaxFor('jobsPerClientTotals').client_name}}
            <span class="text-base font-medium">{{getMaxFor('jobsPerClientTotals').total}} jobs</span>
          </template>
        </stats-card>

        <stats-card
          :loading="loading"
          class="col-span-1"
          :small-title="'Most popular product'"
          type="primary"
          icon="nc-icon nc-basket">
          <template v-slot:title>
            {{getMaxFor('jobsPerProductTotals').product_name}}
            <span class="text-base font-medium">used {{getMaxFor('jobsPerProductTotals').total}} times</span>
          </template>
        </stats-card>

        <stats-card
          :loading="loading"
          :title="jobsPerClientTotals.results.length"
          :previous-title="previousJobsPerClientTotals.results.length"
          class="col-span-1"
          :small-title="'Total clients with posted jobs'"
          type="warning"
          icon="nc-icon nc-circle-10">
        </stats-card>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <bar-chart class="col-span-2 bg-white overflow-hidden shadow rounded-lg mb-4 p-4"
                   :chart-data="jobsPerMonth"></bar-chart>

        <line-chart class="col-span-2 bg-white overflow-hidden shadow rounded-lg mb-4 p-4"
                    :chart-data="eventsPerMonth"
                    :options="{
                      tooltips: {
                       mode: 'index'
                      }
                    }">
        </line-chart>
      </div>
    </div>
  </div>
</template>
<script>
  import ReportDatePicker from 'src/views/admin/reports/ReportDatePicker'
  import format from 'date-fns/format'
  import parseISO from 'date-fns/parseISO'
  import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
  import subDays from 'date-fns/subDays'
  import get from 'lodash/get'
  import ReportService from 'src/services/ReportService'
  import StatsCard from 'src/components/Cards/StatsCard'
  import BarChart from 'src/components/Charts/BarChart'
  import LineChart from 'src/components/Charts/LineChart'
  import ReportsWarning from 'src/views/admin/reports/ReportsWarning'
  import { getDefaultRange } from '@/views/admin/reports/defaultReportRange'

  export default {
    name: 'job-reports',
    components: {
      StatsCard,
      BarChart,
      LineChart,
      ReportDatePicker,
      ReportsWarning,
    },
    data () {
      const year = new Date().getFullYear()
      const defaultRange = getDefaultRange()
      return {
        year,
        defaultRange,
        dateRange: [...defaultRange],
        previousJobTotals: {
          results: [],
        },
        jobTotals: {
          results: [],
        },
        eventTotals: {
          results: [],
        },
        previousJobsPerClientTotals: {
          results: []
        },
        jobsPerClientTotals: {
          results: []
        },
        jobsPerProductTotals: {
          results: []
        },
        loading: false,
      }
    },
    computed: {
      dateFrom () {
        return get(this.dateRange, '[0]', this.defaultRange[0])
      },
      dateTill () {
        return get(this.dateRange, '[1]', this.defaultRange[1])
      },
      previousDateFrom () {
        const from = parseISO(this.dateFrom)
        const till = parseISO(this.dateTill)
        const difference = differenceInCalendarDays(till, from)
        return format(subDays(from, difference), 'yyyy-MM-dd')
      },
      previousDateTill () {
        return this.dateFrom
      },
      jobReportParams () {
        return {
          model: 'jobs',
          group: 'month',
          select: 'id,total',
          date_range: 'custom',
          date_from: this.dateFrom,
          date_till: this.dateTill,
          sort: 'date_month',
        }
      },
      previousJobReportParams () {
        return {
          model: 'jobs',
          group: 'month',
          select: 'id,total',
          date_range: 'custom',
          date_from: this.previousDateFrom,
          date_till: this.previousDateTill,
          sort: 'date_month',
        }
      },
      eventReportParams () {
        return {
          model: 'events',
          group: 'month',
          select: 'id,total,views,apply_starts,click_outs',
          date_range: 'custom',
          date_from: this.dateFrom,
          date_till: this.dateTill,
          sort: 'date_month',
        }
      },
      previousEventReportParams () {
        return {
          model: 'events',
          group: 'month',
          select: 'id,total,views,apply_starts,click_outs',
          date_range: 'custom',
          date_from: this.previousDateFrom,
          date_till: this.previousDateTill,
          sort: 'date_month',
        }
      },
      previousJobsPerClientReport () {
        return {
          model: 'jobs',
          group: 'clients',
          select: 'id,total,client',
          date_range: 'custom',
          date_from: this.previousDateFrom,
          date_till: this.previousDateTill,
          sort: 'date_month',
        }
      },
      jobsPerClientReport () {
        return {
          model: 'jobs',
          group: 'clients',
          select: 'id,total,client',
          date_range: 'custom',
          date_from: this.dateFrom,
          date_till: this.dateTill,
          sort: 'date_month',
        }
      },
      jobsPerProductReport () {
        return {
          model: 'jobs',
          group: 'products',
          select: 'id,total,product',
          date_range: 'custom',
          date_from: this.dateFrom,
          date_till: this.dateTill,
          sort: 'date_month',
        }
      },
      jobsPerMonth () {
        return {
          labels: this.jobTotals.results.map(r => this.$formatDate(new Date(this.year, r.date_month - 1, 1), 'MMM')),
          datasets: [
            {
              label: this.$t('reports.postedJobs'),
              backgroundColor: '#0695A2',
              data: this.jobTotals.results.map(r => ({
                x: new Date(this.year, r.date_month - 1, 1),
                y: r.total
              }))
            },
          ]
        }
      },
      eventsPerMonth () {
        return {
          labels: this.eventTotals.results.map(r => this.$formatDate(new Date(this.year, r.date_month - 1, 1), 'MMM')),
          datasets: [
            {
              label: this.$t('reports.apply_views'),
              borderColor: '#0695A2',
              pointBackgroundColor: '#0695A2',
              data: this.eventTotals.results.map(r => ({
                x: new Date(this.year, r.date_month - 1, 1),
                y: r.apply_views
              }))
            },
            {
              label: this.$t('reports.apply_starts'),
              borderColor: '#F9703E',
              pointBackgroundColor: '#F9703E',
              data: this.eventTotals.results.map(r => ({
                x: new Date(this.year, r.date_month - 1, 1),
                y: r.apply_starts
              }))
            },
            {
              label: this.$t('reports.click_outs'),
              borderColor: '#5145cd',
              pointBackgroundColor: '#5145cd',
              data: this.eventTotals.results.map(r => ({
                x: new Date(this.year, r.date_month - 1, 1),
                y: r.click_outs
              }))
            },
          ]
        }
      },
    },
    methods: {
      getTotalFor (field, dataType) {
        let sum = 0
        this[dataType].results.forEach(item => {
          sum += item[field] || 0
        })
        return sum.toFixed(0)
      },
      getMaxFor (dataType) {
        let maxItem = {total: 0}
        this[dataType].results.forEach(item => {
          if (item.total > maxItem.total) {
            maxItem = item
          }
        })
        return maxItem
      },
      async getReports () {
        try {
          this.loading = true
          await Promise.all([
            this.getJobReports(),
            this.getPreviousJobReports(),
            this.getEventReports(),
            this.getPreviousEventReports(),
            this.getPreviousJobsPerClientReports(),
            this.getJobsPerClientReports(),
            this.getJobsPerProductsReports(),
          ])
        } finally {
          this.loading = false
        }
      },
      async getJobReports () {
        const {data} = await ReportService.get(this.jobReportParams)
        this.jobTotals = data
      },
      async getPreviousJobReports () {
        const {data} = await ReportService.get(this.previousJobReportParams)
        this.previousJobTotals = data
      },
      async getEventReports () {
        const {data} = await ReportService.get(this.eventReportParams)
        this.eventTotals = data
      },
      async getPreviousEventReports () {
        const {data} = await ReportService.get(this.previousEventReportParams)
        this.previousEventTotals = data
      },
      async getJobsPerClientReports () {
        const {data} = await ReportService.get(this.jobsPerClientReport)
        this.jobsPerClientTotals = data
      },
      async getPreviousJobsPerClientReports () {
        const {data} = await ReportService.get(this.previousJobsPerClientReport)
        this.previousJobsPerClientTotals = data
      },
      async getJobsPerProductsReports () {
        const {data} = await ReportService.get(this.jobsPerProductReport)
        this.jobsPerProductTotals = data
      }
    },
    mounted () {
      this.getReports()
    }
  }
</script>
<style>
</style>
